import React, {useState} from 'react';
import {Modal, StyleSheet, View} from 'react-native';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  fetchCommands,
  finalizeDeviceReset,
  resetDevice,
} from '@/entities/device/api';
import {removeTerminalVideo} from '@/entities/stream/api';
import type {DeviceCommandRecord} from '@/entities/device/types';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card, IconBubble} from '@/shared/ui/card';
import {Icon} from '@/shared/ui/icons';
import {AppInput} from '@/shared/ui/input';
import {AppText} from '@/shared/ui/text';
import {StatusPill} from '@/shared/ui/status';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Factory reset — the one action here that destroys data, so it is built as a
 * deliberate two-step rather than a button.
 *
 * Step 1 wipes the terminal (personnel, faces, local audit, licence, both
 * server addresses) and reboots it into the pairing screen. Step 2 forgets it
 * in the cloud: releases the licence seats and deletes the device's rows. They
 * are separate on purpose — the operator watches the terminal acknowledge
 * before erasing the record that would let them retry, and step 2 alone
 * releases a terminal that is lost, stolen or already dead.
 *
 * The company's attendance log survives both steps: it belongs to the company,
 * not to the hardware.
 */
export type FactoryResetPanelProps = {
  companyId: string;
  deviceId: string;
  /** Typed back by the operator to confirm — the same name the list shows. */
  deviceName: string;
  /** Called once the cloud side is finalized and the device row is gone. */
  onFinalized?: () => void;
};

const styles = StyleSheet.create({
  root: {gap: spacing(3)},
  card: {gap: spacing(3)},
  head: {flexDirection: 'row', alignItems: 'center', gap: spacing(3)},
  headText: {flex: 1},
  backdrop: {flex: 1, justifyContent: 'center', padding: spacing(5)},
  dialog: {borderRadius: radii.xl, padding: spacing(5), gap: spacing(4)},
  row: {flexDirection: 'row', gap: spacing(2)},
  flex: {flex: 1},
  bullets: {gap: spacing(1)},
  queued: {gap: spacing(3)},
});

/** The queued wipe, if this device has one in its recent history. */
function findResetCommand(
  commands: DeviceCommandRecord[],
  commandId: string | null,
): DeviceCommandRecord | undefined {
  if (!commandId) {
    return undefined;
  }
  return commands.find(c => c.id === commandId);
}

export function FactoryResetPanel({
  companyId,
  deviceId,
  deviceName,
  onFinalized,
}: FactoryResetPanelProps) {
  const {t} = useTranslation();
  const p = useTheme();
  const qc = useQueryClient();

  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [commandId, setCommandId] = useState<string | null>(null);
  const [finalized, setFinalized] = useState(false);

  // Only polls while a wipe is in flight — the ack is what unlocks step 2.
  const commandsQuery = useQuery({
    queryKey: ['device-commands', companyId, deviceId],
    queryFn: () => fetchCommands(companyId, deviceId),
    enabled: !!commandId && !finalized,
    refetchInterval: 3000,
  });

  const queueReset = useMutation({
    mutationFn: () => resetDevice(companyId, deviceId),
    onSuccess: result => {
      setCommandId(result.commandId);
      setConfirming(false);
      setTyped('');
      void qc.invalidateQueries({
        queryKey: ['device-commands', companyId, deviceId],
      });
    },
  });

  const finalize = useMutation({
    mutationFn: async () => {
      await finalizeDeviceReset(companyId, deviceId);
      // Видео терминала — отдельный источник (см. removeTerminalVideo).
      await removeTerminalVideo(companyId, {device_id: deviceId, device_name: deviceName});
    },
    onSuccess: () => {
      setFinalized(true);
      void qc.invalidateQueries({queryKey: ['devices', companyId]});
      void qc.invalidateQueries({queryKey: ['stream-cameras', companyId]});
      onFinalized?.();
    },
  });

  const command = findResetCommand(commandsQuery.data?.commands ?? [], commandId);
  const acked = command?.status === 'acked';
  const failed = command?.status === 'failed';
  // Терминал не забрал сброс за сутки — облако его сняло (C8).
  const expired = command?.status === 'expired';
  const nameMatches = typed.trim() === deviceName.trim();

  if (finalized) {
    return (
      <Banner
        tone="success"
        title={t('reset.finalized')}
        message={t('reset.finalizedHint')}
      />
    );
  }

  return (
    <View style={styles.root}>
      {/* The one card in the app outlined in the destructive tone: everything inside
          it erases something, and the border is the warning that arrives before the
          text is read. */}
      <Card padding={4} style={[styles.card, {borderColor: p.destructive, borderWidth: 1}]}>
        <View style={styles.head}>
          <IconBubble tone={p.destructiveSoft} size={42} square>
            <Icon name="warning" size={21} color={p.destructive} />
          </IconBubble>
          <AppText variant="heading" style={styles.headText}>
            {t('reset.what')}
          </AppText>
        </View>
        <View style={styles.bullets}>
          <AppText variant="caption">{t('reset.wipesList')}</AppText>
          <AppText variant="caption">{t('reset.keepsLogs')}</AppText>
        </View>

        {commandId ? (
          <View style={styles.queued}>
            <StatusPill
              tone={failed || expired ? 'offline' : acked ? 'online' : 'pending'}
              label={t(
                failed
                  ? 'reset.stateFailed'
                  : expired
                    ? 'reset.stateExpired'
                    : acked
                      ? 'reset.stateAcked'
                      : 'reset.stateQueued',
              )}
            />
            <AppText variant="caption">
              {t(acked ? 'reset.ackedHint' : 'reset.queuedHint')}
            </AppText>
            <AppButton
              title={t('reset.finalize')}
              tone="destructive"
              icon="trash"
              loading={finalize.isPending}
              onPress={() => finalize.mutate()}
            />
          </View>
        ) : (
          <AppButton
            title={t('reset.start')}
            tone="destructive"
            icon="refresh"
            onPress={() => setConfirming(true)}
          />
        )}

        {queueReset.isError || finalize.isError ? (
          <Banner tone="error" title={t('errors.unknown')} />
        ) : null}
      </Card>

      {/* Releasing a terminal that will never answer: skip step 1 entirely. */}
      {!commandId ? (
        <AppButton
          title={t('reset.finalizeOnly')}
          tone="ghost"
          loading={finalize.isPending}
          onPress={() => finalize.mutate()}
        />
      ) : null}

      <Modal
        visible={confirming}
        transparent
        animationType="fade"
        onRequestClose={() => setConfirming(false)}>
        <View style={[styles.backdrop, {backgroundColor: p.overlay}]}>
          <View style={[styles.dialog, {backgroundColor: p.elevated}]}>
            <AppText variant="title">{t('reset.confirmTitle')}</AppText>
            <AppText variant="body" color={p.mutedForeground}>
              {t('reset.confirmBody', {name: deviceName})}
            </AppText>
            <AppInput
              label={t('reset.confirmLabel')}
              value={typed}
              onChangeText={setTyped}
              autoCapitalize="none"
              autoCorrect={false}
            />
            <View style={styles.row}>
              <AppButton
                title={t('actions.cancel')}
                tone="neutral"
                style={styles.flex}
                onPress={() => {
                  setConfirming(false);
                  setTyped('');
                }}
              />
              <AppButton
                title={t('reset.confirmAction')}
                tone="destructive"
                style={styles.flex}
                disabled={!nameMatches}
                loading={queueReset.isPending}
                onPress={() => queueReset.mutate()}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}
